import React from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import RefreshRounded from '@mui/icons-material/RefreshRounded';
import ChatRounded from '@mui/icons-material/ChatRounded';
import PeriodComparePicker, {
  type CustomRange,
} from '../../dialing-opportunity/components/PeriodComparePicker';
import { formatAbsolute, formatRelativeTime, parseBackendDate } from '../../../utils/date';
import type { L2CChainSnapshot } from '../../../types/leadToCash';

interface LeadToCashToolbarProps {
  period: CustomRange | null;
  snapshot?: L2CChainSnapshot | null;
  loading?: boolean;
  refreshing?: boolean;
  disabled?: boolean;
  onPeriodApply: (range: CustomRange) => void;
  onRerun: () => void;
  chatOpen?: boolean;
  onToggleChat?: () => void;
  showChatToggle?: boolean;
}

export default function LeadToCashToolbar({
  period,
  snapshot,
  loading = false,
  refreshing = false,
  disabled = false,
  onPeriodApply,
  onRerun,
  chatOpen = false,
  onToggleChat,
  showChatToggle = false,
}: LeadToCashToolbarProps) {
  const computedAt = snapshot ? parseBackendDate(snapshot.computed_at) : null;
  const computedRelative = computedAt ? formatRelativeTime(computedAt) : null;
  const computedAbsolute = computedAt ? formatAbsolute(computedAt) : null;

  const updatedTooltip = computedAbsolute
    ? `Calculated ${computedAbsolute}${snapshot?.as_of_date ? ` · Warehouse as of ${snapshot.as_of_date}` : ''}`
    : 'Calculation time unavailable';

  const updatedCaption = [
    computedRelative ? `Updated ${computedRelative}` : null,
    snapshot?.from_cache ? 'cached' : null,
    snapshot?.narrative_source === 'llm' ? 'AI summary' : null,
  ].filter(Boolean).join(' · ');

  const updateButtonLabel = refreshing ? 'Updating…' : 'Re-run';

  return (
    <Paper
      variant="outlined"
      sx={(t) => ({
        px: { xs: 1.5, sm: 2 },
        py: 1,
        mb: 1.5,
        borderRadius: `${t.ds?.radius?.md ?? 8}px`,
      })}
    >
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 1,
          rowGap: 1,
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <PeriodComparePicker
            value={period}
            activeStart={snapshot?.current_period?.start ?? null}
            activeEnd={snapshot?.current_period?.end ?? null}
            onApply={onPeriodApply}
            disabled={disabled}
            compareRequired
          />
          {snapshot ? (
            <Tooltip
              title={
                refreshing
                  ? 'Loading updated data from the warehouse'
                  : snapshot.from_cache
                    ? 'Recalculate from warehouse (bypasses cache)'
                    : 'Refresh data for this period'
              }
              arrow
            >
              <span>
                <Button
                  size="small"
                  variant={snapshot.from_cache && !refreshing ? 'contained' : 'outlined'}
                  startIcon={
                    refreshing ? (
                      <CircularProgress size={14} color="inherit" />
                    ) : (
                      <RefreshRounded />
                    )
                  }
                  onClick={onRerun}
                  disabled={disabled || loading}
                >
                  {updateButtonLabel}
                </Button>
              </span>
            </Tooltip>
          ) : null}
        </Stack>

        {snapshot ? (
          <Stack
            direction="row"
            spacing={0.75}
            alignItems="center"
            flexShrink={0}
            sx={{ ml: { xs: 0, sm: 'auto' } }}
          >
            {updatedCaption ? (
              <Tooltip title={updatedTooltip} arrow>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  noWrap
                  sx={{ cursor: 'default', maxWidth: 240 }}
                >
                  {updatedCaption}
                </Typography>
              </Tooltip>
            ) : null}
            {showChatToggle && onToggleChat ? (
              <Tooltip title={chatOpen ? 'Hide assistant' : 'Open assistant'} arrow>
                <Button
                  size="small"
                  variant={chatOpen ? 'contained' : 'outlined'}
                  startIcon={<ChatRounded />}
                  onClick={onToggleChat}
                  disabled={disabled}
                >
                  {chatOpen ? 'Hide chat' : 'Chat'}
                </Button>
              </Tooltip>
            ) : null}
          </Stack>
        ) : null}
      </Box>
    </Paper>
  );
}
